import React, { useState, useRef, useEffect, useCallback } from "react";
import { userMeetsPlan } from "./PlanGate";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import useExternalCalendarEvents, { filterEventsForDate, fmtSyncedTime, PROVIDER_LABEL, PROVIDER_GLYPH } from "../hooks/useExternalCalendarEvents";
import AllTasksView from "./AllTasksView";
import { isTaskOverdue } from "../utils/taskTime"; // Block D20 Part A: overdue in the user's STORED timezone
import { PRIORITY_COLORS as PRIORITY, PRIORITY_RANK } from "../utils/priorityColors";

// ── Design tokens ─────────────────────────────────────────────────────────────
const NAVY = "#0C2D48";
const FONT = "'Raleway', sans-serif";
const BORDER = "#e0e0e0";

const COLS = [
  { id: "todo", label: "To Do", accent: "#5b3fa0", icon: "📋" },
  { id: "inProgress", label: "In Progress", accent: "#f59e0b", icon: "⏳" },
  { id: "done", label: "Completed", accent: "#22c55e", icon: "✅" },
];

const VIEW_ICONS = { kanban: "⊞", list: "≡", priority: "⚑" };

const TIME_PERIODS = [
  { id: "morning", label: "Morning", icon: "🌅", from: 0, to: 12 },
  { id: "afternoon", label: "Afternoon", icon: "☀️", from: 12, to: 17 },
  { id: "evening", label: "Evening", icon: "🌙", from: 17, to: 24 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function getStatus(task) {
  if (task.completed) return "done";
  if (task.inProgress) return "inProgress";
  return "todo";
}

// Block D20 Part A: overdue in the user's STORED timezone (set on render in TaskBoard).
function isOverdue(task) { return isTaskOverdue(task, isOverdue.tz); }

function fmtTime(dt) {
  if (!dt || isNaN(new Date(dt).getTime())) return null;
  return new Date(dt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
}

function hasScheduledTime(task) {
  if (!task.datetime) return false;
  const d = new Date(task.datetime);
  if (isNaN(d.getTime())) return false;
  // treat midnight (00:00) with no explicit time as "no time"
  return !(d.getHours() === 0 && d.getMinutes() === 0 && !task.time);
}

function sortByPriorityTime(list) {
  return [...list].sort((a, b) => {
    const pa = PRIORITY_RANK[a.priority] ?? 2, pb = PRIORITY_RANK[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    return new Date(a.datetime || 0) - new Date(b.datetime || 0);
  });
}
function sortByTime(list) {
  return [...list].sort((a, b) => new Date(a.datetime || 0) - new Date(b.datetime || 0));
}

// Merge timed tasks and timed synced events into per-period item arrays, sorted chronologically
function buildPeriodItems(tasks, syncedEvents) {
  const timedTasks = tasks.filter(hasScheduledTime).map(t => ({
    type: 'task', time: new Date(t.datetime), data: t,
  }));
  const timedEvents = (syncedEvents || []).filter(e => !e.allDay && e.startTime).map(e => ({
    type: 'event', time: new Date(e.startTime), data: e,
  }));
  const allTimed = [...timedTasks, ...timedEvents].sort((a, b) => a.time - b.time);
  return TIME_PERIODS.map(p => ({
    ...p,
    items: allTimed.filter(item => { const h = item.time.getHours(); return h >= p.from && h < p.to; }),
  })).filter(p => p.items.length > 0);
}

function statusToUpdates(statusId) {
  if (statusId === "done") return { completed: true, inProgress: false };
  if (statusId === "inProgress") return { completed: false, inProgress: true };
  return { completed: false, inProgress: false };
}

// ── Shared sub-components ─────────────────────────────────────────────────────
function PriorityDot({ priority, size = 8 }) {
  const cfg = PRIORITY[priority] || PRIORITY.Medium;
  return <span style={{ width: size, height: size, borderRadius: "50%", background: cfg.color, flexShrink: 0, display: "inline-block" }} />;
}

function PriorityBadge({ priority }) {
  const cfg = PRIORITY[priority] || PRIORITY.Medium;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.color}`, borderRadius: 6, padding: "0.1rem 0.45rem", fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
      <PriorityDot priority={priority} size={6} />
      {priority || "Medium"}
    </span>
  );
}

function ProjectChip({ name, color }) {
  return (
    <span style={{ fontSize: "0.7rem", color: color ? "#333" : "#666", background: color ? color + "22" : "#f0f4f8", border: `1px solid ${color || "#dde6ef"}`, borderRadius: 5, padding: "0.1rem 0.4rem", fontWeight: 600, maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>
      {name}
    </span>
  );
}

function OverdueBadge() {
  const { t } = useTranslation('common');
  return <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#dc2626", background: "rgba(220,38,38,0.08)", borderRadius: 5, padding: "0.1rem 0.4rem", flexShrink: 0 }}>{t('tasks.overdue', 'Overdue')}</span>;
}

// ── Client work toast ─────────────────────────────────────────────────────────
function ClientWorkToast({ task, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 6000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 9999, background: "#fff", border: "1px solid #fbbf24", borderLeft: "4px solid #f59e0b", borderRadius: 10, padding: "0.75rem 1rem", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", fontFamily: FONT, maxWidth: 320, display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>⏱️</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#92400e", marginBottom: "0.15rem" }}>This is client work</div>
        <div style={{ fontSize: "0.8rem", color: "#555" }}>Don't forget to start your timer for <strong>{task?.title}</strong>.</div>
      </div>
      <button onClick={onDismiss} style={{ background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: "0.85rem", padding: 0, lineHeight: 1, flexShrink: 0 }}>✕</button>
    </div>
  );
}

function TimerBtn({ taskId, task, activeTimers, onToggle, loggedMinutes }) {
  const running = !!activeTimers[taskId];
  const secs = activeTimers[taskId]?.elapsed || 0;
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }} onClick={e => e.stopPropagation()}>
      <button onClick={() => onToggle(taskId, task)} style={{ background: running ? "rgba(233,106,106,0.1)" : "rgba(12,45,72,0.06)", border: `1px solid ${running ? "#E96A6A" : "#ddd"}`, color: running ? "#E96A6A" : "#666", borderRadius: 5, padding: "0.1rem 0.45rem", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>
        {running ? "⏹ Stop" : "▶ Start"}
      </button>
      {secs > 0 && <span style={{ fontSize: "0.7rem", color: "#999", fontFamily: "monospace" }}>{mm}:{ss}</span>}
      {!running && loggedMinutes > 0 && (
        <span style={{ fontSize: "0.65rem", color: "#aaa", fontFamily: "monospace" }}>
          {Math.floor(loggedMinutes / 60) > 0 ? `${Math.floor(loggedMinutes / 60)}h ` : ""}{loggedMinutes % 60 > 0 ? `${loggedMinutes % 60}m` : ""} logged
        </span>
      )}
    </div>
  );
}

function StatusCycleBtn({ task, onMove }) {
  const { t } = useTranslation('common');
  const status = getStatus(task);
  const next = status === "todo" ? "inProgress" : status === "inProgress" ? "done" : "todo";
  const labels = {
    todo: t('tasks.status.todo'),
    inProgress: t('tasks.status.in_progress'),
    done: t('tasks.status.done'),
  };
  const colors = { todo: "#5b3fa0", inProgress: "#f59e0b", done: "#22c55e" };
  return (
    <button onClick={e => { e.stopPropagation(); onMove(task.id, statusToUpdates(next)); }} title={`Move to ${labels[next]}`} style={{ background: colors[status] + "15", border: `1px solid ${colors[status]}`, color: colors[status], borderRadius: 6, padding: "0.15rem 0.55rem", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap", flexShrink: 0 }}>
      {labels[status]}
    </button>
  );
}

// ── View toggle ───────────────────────────────────────────────────────────────
function ViewToggle({ view, onChange }) {
  return (
    <div className="view-toggle" style={{ display: "flex", border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden" }}>
      {["kanban", "list", "priority"].map(v => (
        <button key={v} onClick={() => onChange(v)} title={v.charAt(0).toUpperCase() + v.slice(1)} style={{ padding: "0.4rem 0.7rem", background: view === v ? NAVY : "#fff", color: view === v ? "#fff" : "#666", border: "none", borderRight: v !== "priority" ? `1px solid ${BORDER}` : "none", cursor: "pointer", fontSize: "1rem", fontFamily: FONT, display: "flex", alignItems: "center", gap: "0.3rem", fontWeight: view === v ? 700 : 400 }}>
          <span>{VIEW_ICONS[v]}</span>
          <span className="view-toggle-label" style={{ fontSize: "0.75rem" }}>{v.charAt(0).toUpperCase() + v.slice(1)}</span>
        </button>
      ))}
    </div>
  );
}

// ── Filter pills ──────────────────────────────────────────────────────────────
const FILTERS = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "overdue", label: "Overdue" },
  { id: "high", label: "High Priority" },
];

function FilterPills({ active, onChange, counts }) {
  return (
    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
      {FILTERS.map(f => (
        <button key={f.id} onClick={() => onChange(f.id)} style={{ padding: "0.28rem 0.8rem", borderRadius: 20, border: active === f.id ? `1.5px solid ${NAVY}` : "1px solid #ddd", background: active === f.id ? NAVY : "#fff", color: active === f.id ? "#fff" : "#555", fontSize: "0.8rem", fontWeight: active === f.id ? 700 : 500, cursor: "pointer", fontFamily: FONT }}>
          {f.label}
          {counts?.[f.id] > 0 && <span style={{ marginLeft: "0.3rem", fontSize: "0.7rem", opacity: 0.8 }}>({counts[f.id]})</span>}
        </button>
      ))}
    </div>
  );
}

// ── Kanban card ───────────────────────────────────────────────────────────────
function KanbanCard({ task, onView, isPro, activeTimers, onTimerToggle, taskTypes, loggedMinutes }) {
  const overdue = isOverdue(task);
  const time = fmtTime(task.datetime);
  const status = getStatus(task);
  return (
    <div
      draggable
      onDragStart={e => e.dataTransfer.setData("taskId", String(task.id))}
      onClick={() => onView(task)}
      style={{ background: "#fff", border: `1px solid ${overdue ? "rgba(220,38,38,0.3)" : BORDER}`, borderLeft: `3px solid ${(PRIORITY[task.priority] || PRIORITY.Medium).color}`, borderRadius: 9, padding: "0.7rem 0.8rem", cursor: "grab", userSelect: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", opacity: status === "done" ? 0.65 : 1, fontFamily: FONT }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(12,45,72,0.10)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.4rem", marginBottom: "0.4rem" }}>
        <span style={{ fontWeight: 700, fontSize: "0.88rem", color: NAVY, lineHeight: 1.3, flex: 1, textDecoration: status === "done" ? "line-through" : "none" }}>{task.title}</span>
        <PriorityBadge priority={task.priority || "Medium"} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", alignItems: "center" }}>
        {task.projectName && <ProjectChip name={task.projectName} color={task.projectColor} />}
        {time && <span style={{ fontSize: "0.7rem", color: "#888" }}>🕐 {time}</span>}
        {overdue && <OverdueBadge />}
        {task.taskType && taskTypes && <span style={{ fontSize: "0.7rem", color: "#999", background: "#f5f5f5", borderRadius: 5, padding: "0.1rem 0.35rem" }}>{taskTypes.find(t => t.value === task.taskType)?.label || task.taskType}</span>}
      </div>
      {isPro && status !== "done" && <div style={{ marginTop: "0.45rem" }}><TimerBtn taskId={task.id} task={task} activeTimers={activeTimers} onToggle={onTimerToggle} loggedMinutes={loggedMinutes?.[String(task.id)] || 0} /></div>}
    </div>
  );
}

// ── Kanban column ─────────────────────────────────────────────────────────────
function KanbanCol({ col, tasks, onView, onDrop, onAddTask, isDragOver, isPro, activeTimers, onTimerToggle, taskTypes, loggedMinutes, isMobileViewport }) {
  return (
    <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); onDrop(e, col.id); }} className="kanban-col-wrapper" style={{ flex: isMobileViewport ? "0 0 85vw" : "1 0 260px", minWidth: isMobileViewport ? "85vw" : 260, maxWidth: isMobileViewport ? "85vw" : 340, display: "flex", flexDirection: "column", background: isDragOver ? col.accent + "0a" : "#f7f8fc", border: isDragOver ? `2px dashed ${col.accent}` : "2px solid transparent", borderRadius: 12, overflow: "hidden", transition: "border 0.15s, background 0.15s" }}>
      <div style={{ padding: "0.8rem 0.9rem 0.6rem", borderBottom: `2px solid ${col.accent}`, background: "#fff", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
          <span style={{ fontSize: "0.85rem", fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: "#333", textTransform: "uppercase", letterSpacing: "0.05em" }}>{col.label}</span>
          <span style={{ background: col.accent, color: "#fff", borderRadius: 10, padding: "0.05rem 0.48rem", fontSize: "0.73rem", fontWeight: 700 }}>{tasks.length}</span>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0.65rem", display: "flex", flexDirection: "column", gap: "0.45rem" }}>
        {tasks.length === 0 ? (
          <div style={{ textAlign: "center", padding: "1.75rem 0.5rem", color: "#777", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.45rem" }}>
            <span style={{ fontSize: "1.5rem" }}>{col.icon}</span>
            <p style={{ margin: 0, fontSize: "0.82rem", fontStyle: "italic", fontFamily: FONT }}>{col.id === "done" ? "Nothing completed yet" : `Nothing ${col.id === "inProgress" ? "in progress" : "to do"} yet`}</p>
            {col.id !== "done" && <button onClick={onAddTask} style={{ background: "#E4E69B", border: "none", color: "#333", borderRadius: 7, padding: "0.28rem 0.7rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>+ Add task</button>}
          </div>
        ) : tasks.map(task => (
          <KanbanCard key={task.id} task={task} onView={onView} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
        ))}
      </div>
    </div>
  );
}

// ── Mobile viewport hook (640px breakpoint) ───────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = e => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mobile;
}

// ── List row ──────────────────────────────────────────────────────────────────
function ListRow({ task, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, loggedMinutes }) {
  const overdue  = isOverdue(task);
  const time     = fmtTime(task.datetime);
  const cfg      = PRIORITY[task.priority] || PRIORITY.Medium;
  const isMobile = useIsMobile();
  const running  = !!activeTimers[task.id];

  if (isMobile) {
    return (
      <div
        onClick={() => onView(task)}
        style={{ borderRadius: 8, background: "#fff", border: `1px solid ${BORDER}`, borderLeft: `3px solid ${cfg.color}`, cursor: "pointer", opacity: task.completed ? 0.6 : 1, fontFamily: FONT, padding: "0.55rem 0.75rem" }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(12,45,72,0.08)"; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
      >
        {/* Line 1: priority dot + title, wraps up to 2 lines */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.45rem", marginBottom: "0.35rem" }}>
          <span style={{ marginTop: "0.22rem", flexShrink: 0, lineHeight: 1 }}>
            <PriorityDot priority={task.priority || "Medium"} size={9} />
          </span>
          <span style={{ fontWeight: 600, fontSize: "0.88rem", color: NAVY, minWidth: 0, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", textDecoration: task.completed ? "line-through" : "none", lineHeight: 1.35 }}>
            {task.title}
          </span>
        </div>
        {/* Line 2: time/badge left, icon actions right */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", paddingLeft: "1.15rem" }}>
          {time && <span style={{ fontSize: "0.72rem", color: "#888", whiteSpace: "nowrap" }}>🕐 {time}</span>}
          {overdue && <OverdueBadge />}
          <div style={{ flex: 1 }} />
          {isPro && !task.completed && (
            <div onClick={e => e.stopPropagation()}>
              <button
                onClick={() => onTimerToggle(task.id, task)}
                style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: running ? "rgba(233,106,106,0.1)" : "rgba(12,45,72,0.06)", border: `1px solid ${running ? "#E96A6A" : "#ddd"}`, color: running ? "#E96A6A" : "#666", borderRadius: 8, cursor: "pointer", fontSize: "1rem", flexShrink: 0 }}
              >
                {running ? "⏹" : "▶"}
              </button>
            </div>
          )}
          {!task.completed && (
            <button
              onClick={e => { e.stopPropagation(); onMove(task.id, { completed: true, inProgress: false }); }}
              style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.4)", color: "#16a34a", borderRadius: 8, cursor: "pointer", fontSize: "1.1rem", flexShrink: 0 }}
            >
              ✓
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div onClick={() => onView(task)} style={{ display: "flex", alignItems: "center", gap: "0.65rem", padding: "0.6rem 0.85rem", borderRadius: 8, background: "#fff", border: `1px solid ${BORDER}`, borderLeft: `3px solid ${cfg.color}`, cursor: "pointer", opacity: task.completed ? 0.6 : 1, fontFamily: FONT }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(12,45,72,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
    >
      <PriorityDot priority={task.priority || "Medium"} size={9} />
      <span style={{ fontWeight: 600, fontSize: "0.88rem", color: NAVY, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: task.completed ? "line-through" : "none" }}>
        {task.title}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {task.projectName && <ProjectChip name={task.projectName} color={task.projectColor} />}
        {time && <span style={{ fontSize: "0.72rem", color: "#888", whiteSpace: "nowrap" }}>🕐 {time}</span>}
        {overdue && <OverdueBadge />}
        {isPro && !task.completed && <TimerBtn taskId={task.id} task={task} activeTimers={activeTimers} onToggle={onTimerToggle} loggedMinutes={loggedMinutes?.[String(task.id)] || 0} />}
        {!task.completed && <button onClick={e => { e.stopPropagation(); onMove(task.id, { completed: true, inProgress: false }); }} style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.4)", color: "#16a34a", borderRadius: 5, padding: "0.1rem 0.45rem", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>✓ Done</button>}
        <StatusCycleBtn task={task} onMove={onMove} />
      </div>
    </div>
  );
}

// ── Priority section (collapsible) ────────────────────────────────────────────
function PrioritySection({ priority, tasks, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, onAddTask, dayLabel, loggedMinutes }) {
  const [open, setOpen] = useState(true);
  const cfg = PRIORITY[priority] || PRIORITY.Medium;
  return (
    <div style={{ marginBottom: "0.65rem" }}>
      <button onClick={() => setOpen(v => !v)} style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.55rem", background: cfg.bg, border: `1px solid ${cfg.color}`, borderRadius: open ? "8px 8px 0 0" : 8, padding: "0.55rem 0.85rem", cursor: "pointer", fontFamily: FONT }}>
        <PriorityDot priority={priority} size={10} />
        <span style={{ fontWeight: 700, fontSize: "0.82rem", color: cfg.text, textTransform: "uppercase", letterSpacing: "0.05em", flex: 1, textAlign: "left" }}>{priority} Priority</span>
        <span style={{ fontSize: "0.75rem", color: cfg.text, fontWeight: 700 }}>{tasks.length} task{tasks.length !== 1 ? "s" : ""}</span>
        <span style={{ color: cfg.text, fontSize: "0.8rem", transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s" }}>▼</span>
      </button>
      {open && (
        <div style={{ border: `1px solid ${cfg.color}`, borderTop: "none", borderRadius: "0 0 8px 8px", padding: "0.5rem", background: "#fafafa", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          {tasks.length === 0 ? (
            <div style={{ textAlign: "center", padding: "0.75rem", color: "#777", fontSize: "0.83rem", fontStyle: "italic" }}>
              No {priority.toLowerCase()} priority tasks {dayLabel}{" "}
              <button onClick={onAddTask} style={{ background: "none", border: "none", color: cfg.color, cursor: "pointer", fontWeight: 700, fontFamily: FONT, fontSize: "0.83rem" }}>+ Add one</button>
            </div>
          ) : tasks.map(task => (
            <ListRow key={task.id} task={task} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Synced event row (read-only, visually distinct from task rows) ────────────
function SyncedEventRow({ event }) {
  const isMobile = useIsMobile();
  const glyph = PROVIDER_GLYPH[event.provider] || '📅';
  const providerLabel = PROVIDER_LABEL[event.provider] || 'Calendar';
  const timeStr = event.startTime ? fmtSyncedTime(event.startTime) : null;

  if (isMobile) {
    return (
      <div style={{ borderRadius: 8, background: "rgba(100,116,139,0.05)", border: "1px solid rgba(100,116,139,0.14)", padding: "0.55rem 0.75rem", fontFamily: FONT, cursor: "default", userSelect: "none" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.45rem", marginBottom: "0.25rem" }}>
          <span style={{ fontSize: "0.8rem", opacity: 0.65, flexShrink: 0, marginTop: "0.1rem" }}>{glyph}</span>
          <span style={{ fontSize: "0.88rem", fontWeight: 500, color: "#64748b", minWidth: 0, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: 1.35 }}>
            {event.title || 'Untitled event'}
          </span>
        </div>
        <div style={{ paddingLeft: "1.15rem", fontSize: "0.72rem", color: "#94a3b8" }}>
          {[timeStr, providerLabel].filter(Boolean).join(' · ')}
        </div>
      </div>
    );
  }

  return (
    <div
      title={`${event.title || 'Event'}${timeStr ? '\n' + timeStr : ''}\nSynced from ${providerLabel}`}
      style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.6rem", borderRadius: 8, background: "rgba(100,116,139,0.05)", border: "1px solid rgba(100,116,139,0.14)", cursor: "default", userSelect: "none", fontFamily: FONT }}
    >
      <span style={{ fontSize: "0.8rem", opacity: 0.65, flexShrink: 0 }}>{glyph}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.82rem", fontWeight: 500, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {event.title || 'Untitled event'}
        </div>
        {timeStr && <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: "0.05rem" }}>{timeStr} · {providerLabel}</div>}
      </div>
    </div>
  );
}

// ── All-day event banner (pinned above timed periods) ─────────────────────────
function AllDayBanner({ events }) {
  if (!events.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginBottom: "0.55rem" }}>
      {events.map((ev, i) => {
        const glyph = PROVIDER_GLYPH[ev.provider] || '📅';
        return (
          <div key={ev.id || i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.25rem 0.75rem", borderRadius: 6, background: "rgba(100,116,139,0.07)", border: "1px solid rgba(100,116,139,0.16)", fontFamily: FONT, userSelect: "none" }}>
            <span style={{ fontSize: "0.75rem", opacity: 0.6 }}>{glyph}</span>
            <span style={{ fontSize: "0.79rem", fontWeight: 500, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              All day: {ev.title || 'Untitled event'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Shared period section renderer (tasks + events interleaved) ───────────────
function PeriodSection({ period, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, loggedMinutes }) {
  return (
    <div style={{ marginBottom: "0.65rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.3rem 0.5rem 0.4rem", marginBottom: "0.35rem" }}>
        <span style={{ fontSize: "0.88rem" }}>{period.icon}</span>
        <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: FONT }}>{period.label}</span>
        <span style={{ fontSize: "0.72rem", color: "#aaa", fontFamily: FONT }}>
          {period.from === 0 ? "before noon" : period.from === 12 ? "noon–5 pm" : "after 5 pm"}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
        {period.items.map(item =>
          item.type === 'task'
            ? <ListRow key={`t-${item.data.id}`} task={item.data} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
            : <SyncedEventRow key={`e-${item.data.id || item.data.externalId}`} event={item.data} />
        )}
      </div>
    </div>
  );
}

// ── Today list view ───────────────────────────────────────────────────────────
function TodayListView({ filtered, syncedEvents, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, onAddTask, loggedMinutes }) {
  const unscheduled = sortByPriorityTime(filtered.filter(t => !hasScheduledTime(t)));
  const allDayEvents = (syncedEvents || []).filter(e => e.allDay);
  const byPeriod = buildPeriodItems(filtered, syncedEvents);
  const [unschedOpen, setUnschedOpen] = useState(true);

  if (filtered.length === 0 && (!syncedEvents || syncedEvents.length === 0)) return <EmptyState onAddTask={onAddTask} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
      <AllDayBanner events={allDayEvents} />
      {unscheduled.length > 0 && (
        <div style={{ marginBottom: "0.65rem" }}>
          <button onClick={() => setUnschedOpen(v => !v)} style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.5rem", background: "#f0f4f8", border: "1px solid #dde6ef", borderRadius: unschedOpen ? "8px 8px 0 0" : 8, padding: "0.55rem 0.85rem", cursor: "pointer", fontFamily: FONT }}>
            <span style={{ fontSize: "1rem" }}>📌</span>
            <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#555", textTransform: "uppercase", letterSpacing: "0.05em", flex: 1, textAlign: "left" }}>Unscheduled</span>
            <span style={{ fontSize: "0.75rem", color: "#888", fontWeight: 700 }}>{unscheduled.length} task{unscheduled.length !== 1 ? "s" : ""}</span>
            <span style={{ color: "#aaa", fontSize: "0.8rem", transform: unschedOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s" }}>▼</span>
          </button>
          {unschedOpen && (
            <div style={{ border: "1px solid #dde6ef", borderTop: "none", borderRadius: "0 0 8px 8px", padding: "0.5rem", background: "#fafafa", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {unscheduled.map(task => (
                <ListRow key={task.id} task={task} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
              ))}
            </div>
          )}
        </div>
      )}
      {byPeriod.map(period => (
        <PeriodSection key={period.id} period={period} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
      ))}
    </div>
  );
}

// ── Tomorrow list view (special) ──────────────────────────────────────────────
function TomorrowListView({ filtered, syncedEvents, onView, onMove, isPro, activeTimers, onTimerToggle, taskTypes, onAddTask, loggedMinutes }) {
  const unscheduled = sortByPriorityTime(filtered.filter(t => !hasScheduledTime(t)));
  const allDayEvents = (syncedEvents || []).filter(e => e.allDay);
  const byPeriod = buildPeriodItems(filtered, syncedEvents);
  const [unschedOpen, setUnschedOpen] = useState(true);

  if (filtered.length === 0 && (!syncedEvents || syncedEvents.length === 0)) return <EmptyState onAddTask={onAddTask} />;

  const scheduledTaskCount = filtered.filter(hasScheduledTime).length;
  const timedEventCount = (syncedEvents || []).filter(e => !e.allDay && e.startTime).length;
  const timedTotal = scheduledTaskCount + timedEventCount;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
      <AllDayBanner events={allDayEvents} />

      {/* Unscheduled section */}
      <div style={{ marginBottom: "0.65rem" }}>
        <button onClick={() => setUnschedOpen(v => !v)} style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.5rem", background: "#f0f4f8", border: "1px solid #dde6ef", borderRadius: unschedOpen ? "8px 8px 0 0" : 8, padding: "0.55rem 0.85rem", cursor: "pointer", fontFamily: FONT }}>
          <span style={{ fontSize: "1rem" }}>📌</span>
          <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#555", textTransform: "uppercase", letterSpacing: "0.05em", flex: 1, textAlign: "left" }}>Unscheduled</span>
          <span style={{ fontSize: "0.75rem", color: "#888", fontWeight: 700 }}>{unscheduled.length} task{unscheduled.length !== 1 ? "s" : ""}</span>
          <span style={{ color: "#aaa", fontSize: "0.8rem", transform: unschedOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.15s" }}>▼</span>
        </button>
        {unschedOpen && (
          <div style={{ border: "1px solid #dde6ef", borderTop: "none", borderRadius: "0 0 8px 8px", padding: "0.5rem", background: "#fafafa", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            {unscheduled.length === 0 ? (
              <div style={{ textAlign: "center", padding: "0.6rem", color: "#aaa", fontSize: "0.83rem", fontStyle: "italic" }}>
                No unscheduled tasks —{" "}
                <button onClick={onAddTask} style={{ background: "none", border: "none", color: NAVY, cursor: "pointer", fontWeight: 700, fontFamily: FONT, fontSize: "0.83rem" }}>+ add one</button>
              </div>
            ) : unscheduled.map(task => (
              <ListRow key={task.id} task={task} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
            ))}
          </div>
        )}
      </div>

      {/* Scheduled tasks + synced events interleaved by time period */}
      {timedTotal > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.25rem 0 0.65rem" }}>
            <div style={{ flex: 1, height: 1, background: "#e0e0e0" }} />
            <span style={{ fontSize: "0.75rem", color: "#aaa", fontWeight: 600, whiteSpace: "nowrap", fontFamily: FONT }}>
              SCHEDULED · {scheduledTaskCount} task{scheduledTaskCount !== 1 ? "s" : ""}{timedEventCount > 0 ? ` + ${timedEventCount} event${timedEventCount !== 1 ? "s" : ""}` : ""}
            </span>
            <div style={{ flex: 1, height: 1, background: "#e0e0e0" }} />
          </div>
          {byPeriod.map(period => (
            <PeriodSection key={period.id} period={period} onView={onView} onMove={onMove} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} taskTypes={taskTypes} loggedMinutes={loggedMinutes} />
          ))}
        </div>
      )}

      {timedTotal === 0 && unscheduled.length > 0 && (
        <div style={{ textAlign: "center", padding: "0.75rem", color: "#aaa", fontSize: "0.83rem", fontStyle: "italic" }}>
          No timed tasks yet —{" "}
          <button onClick={onAddTask} style={{ background: "none", border: "none", color: NAVY, cursor: "pointer", fontWeight: 700, fontFamily: FONT, fontSize: "0.83rem" }}>+ schedule one</button>
        </div>
      )}
    </div>
  );
}

// ── AI plan panel (modal) ─────────────────────────────────────────────────────
function AiPlanPanel({ tasks, date, dateLabel, onClose, onAccept }) {
  const _aiDayStart = new Date(date); _aiDayStart.setHours(0, 0, 0, 0);
  const _aiDayEnd   = new Date(date); _aiDayEnd.setHours(23, 59, 59, 999);
  const { events: _syncedAiEvents } = useExternalCalendarEvents(_aiDayStart.toISOString(), _aiDayEnd.toISOString());

  const [aiLoading, setAiLoading] = useState(true);
  const [suggestions, setSuggestions] = useState(null);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({});
  const [accepted, setAccepted] = useState(false);
  const hasRun = useRef(false);
  const eventsRef = useRef([]);

  const tomorrowStr = date.toDateString();

  // Keep a ref to the latest filtered calendar events
  useEffect(() => {
    eventsRef.current = _syncedAiEvents
      .map(e => ({
        title: e.title || 'Untitled',
        start: e.startTime,
        end: e.endTime || null,
      }))
      .filter(e => {
        if (!e.start) return false;
        try { return new Date(e.start).toDateString() === tomorrowStr; } catch { return false; }
      });
  }, [_syncedAiEvents, tomorrowStr]);

  // Wait for calendar events to finish loading (up to 2 s), then call AI
  useEffect(() => {
    if (hasRun.current) return;
    const delay = _aiDayStart ? 500 : 200;
    const timer = setTimeout(() => {
      if (hasRun.current) return;
      hasRun.current = true;

      const calMeetings = eventsRef.current.map(e => ({
        title: e.title,
        start: e.start ? new Date(e.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true }) : null,
        end: e.end ? new Date(e.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true }) : null,
      }));

      const payload = {
        date: dateLabel,
        calendarMeetings: calMeetings,
        tasks: tasks.map(t => ({
          id: t.id,
          title: t.title,
          priority: t.priority || "Medium",
          currentTime: fmtTime(t.datetime) || null,
          project: t.projectName || null,
          estimatedMinutes: t.estimatedDuration || null,
        })),
      };

      api('/api/aidey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: `You are Aidey, a sharp executive assistant. Analyse the user's tasks and calendar meetings for ${dateLabel}.

Steps:
1. Identify fixed calendar meetings that cannot move.
2. Calculate available time gaps between meetings and at the start/end of the day.
3. Suggest an optimal time slot for each task based on: task priority, estimated duration (default 30 min if unknown), available gaps, and focus type (e.g. deep work needs an uninterrupted block).

Return ONLY valid JSON — no markdown fences, no prose outside the JSON:
{
  "dayOverview": "1-2 sentence summary of how tomorrow looks",
  "suggestions": [
    {
      "taskId": <number or null if unknown>,
      "taskTitle": "exact task title from input",
      "suggestedTime": "9:00 AM",
      "reason": "plain English explanation, e.g. 'Scheduled before your 10am call as it only needs 20 minutes'"
    }
  ]
}

Only include tasks you are actually repositioning or explaining. Omit tasks that are already well placed. Return valid JSON only.`,
          messages: [{ role: 'user', content: JSON.stringify(payload) }],
        }),
      })
        .then(r => r.json())
        .then(d => {
          const raw = (d.reply || '').trim();
          const match = raw.match(/\{[\s\S]*\}/);
          if (!match) throw new Error('no JSON');
          const parsed = JSON.parse(match[0]);
          const suggs = Array.isArray(parsed.suggestions) ? parsed.suggestions : [];
          setOverview(parsed.dayOverview || null);
          setSuggestions(suggs);
          const init = {};
          suggs.forEach(s => { if (s.taskId != null) init[s.taskId] = true; });
          setChecked(init);
          setAiLoading(false);
        })
        .catch(() => {
          setError("Couldn't generate a plan. Check your connection and try again.");
          setAiLoading(false);
        });
    }, delay);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function applySelected() {
    if (!suggestions) return;
    suggestions.forEach(s => {
      if (s.taskId != null && checked[s.taskId] && s.suggestedTime) {
        const parts = s.suggestedTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (parts) {
          let h = parseInt(parts[1]);
          const m = parseInt(parts[2]);
          const ampm = parts[3].toUpperCase();
          if (ampm === 'PM' && h !== 12) h += 12;
          if (ampm === 'AM' && h === 12) h = 0;
          const dt = new Date(date);
          dt.setHours(h, m, 0, 0);
          onAccept(s.taskId, { datetime: dt.toISOString() });
        }
      }
    });
    setAccepted(true);
    setTimeout(onClose, 900);
  }

  const checkedCount = Object.values(checked).filter(Boolean).length;

  // Render current calendar events for display in the modal
  const displayEvents = eventsRef.current;

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
    >
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "82vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(0,0,0,0.18)", fontFamily: FONT, overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: "0.6rem", flexShrink: 0 }}>
          <span style={{ fontSize: "1.2rem" }}>🤖</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: "1rem", color: NAVY, fontFamily: "'Poppins', sans-serif" }}>Aidey's Plan for {dateLabel}</div>
            <div style={{ fontSize: "0.73rem", color: "#999", marginTop: "0.1rem" }}>Select suggestions to apply, then confirm — tasks won't move until you approve.</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#bbb", cursor: "pointer", fontSize: "1.1rem", lineHeight: 1, padding: "0.15rem" }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.25rem" }}>

          {aiLoading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem", padding: "2.5rem 1rem", color: "#777", fontSize: "0.88rem", textAlign: "center" }}>
              <span style={{ fontSize: "2rem" }}>⏳</span>
              <div>Analysing your tasks and calendar…</div>
              <div style={{ fontSize: "0.78rem", color: "#bbb" }}>This usually takes a few seconds</div>
            </div>
          )}

          {error && (
            <div style={{ padding: "0.8rem", background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 8, color: "#dc2626", fontSize: "0.86rem" }}>
              {error}
            </div>
          )}

          {accepted && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", padding: "2rem", textAlign: "center" }}>
              <span style={{ fontSize: "2rem" }}>✅</span>
              <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#22c55e" }}>Applied! Tasks have been rescheduled.</div>
            </div>
          )}

          {suggestions && !aiLoading && !accepted && (
            <>
              {overview && (
                <div style={{ background: "#f0f5f9", borderRadius: 9, padding: "0.75rem 0.9rem", marginBottom: "0.9rem", fontSize: "0.86rem", color: "#445", lineHeight: 1.55, borderLeft: `3px solid ${NAVY}` }}>
                  {overview}
                </div>
              )}

              {displayEvents.length > 0 && (
                <div style={{ marginBottom: "0.9rem" }}>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.4rem" }}>Fixed meetings</div>
                  {displayEvents.slice(0, 6).map((e, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.81rem", color: "#666", padding: "0.2rem 0" }}>
                      <span>📌</span>
                      <span style={{ fontWeight: 600 }}>{e.title}</span>
                      {e.start && (
                        <span style={{ color: "#aaa" }}>
                          · {new Date(e.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })}
                          {e.end && ` – ${new Date(e.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })}`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.5rem" }}>
                Suggestions ({suggestions.length})
              </div>

              {suggestions.length === 0 ? (
                <div style={{ textAlign: "center", padding: "1.5rem", color: "#888", fontSize: "0.86rem", fontStyle: "italic" }}>
                  Your tasks look well-arranged. No changes suggested.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {suggestions.map((s, i) => (
                    <label
                      key={i}
                      style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", background: "#fafafa", border: `1px solid ${s.taskId != null && checked[s.taskId] ? "#c8dce9" : "#eee"}`, borderRadius: 9, padding: "0.75rem 0.85rem", cursor: s.taskId != null ? "pointer" : "default", transition: "border-color 0.1s" }}
                    >
                      {s.taskId != null ? (
                        <input
                          type="checkbox"
                          checked={!!checked[s.taskId]}
                          onChange={e => setChecked(prev => ({ ...prev, [s.taskId]: e.target.checked }))}
                          style={{ marginTop: "0.2rem", accentColor: NAVY, cursor: "pointer", flexShrink: 0 }}
                        />
                      ) : (
                        <span style={{ width: 14, flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: "0.88rem", color: NAVY, marginBottom: "0.15rem" }}>{s.taskTitle}</div>
                        {s.suggestedTime && (
                          <div style={{ fontSize: "0.77rem", color: "#5b3fa0", fontWeight: 600, marginBottom: "0.2rem" }}>🕐 {s.suggestedTime}</div>
                        )}
                        <div style={{ fontSize: "0.8rem", color: "#666", lineHeight: 1.45 }}>{s.reason}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {suggestions && !aiLoading && !accepted && (
          <div style={{ padding: "0.85rem 1.25rem", borderTop: "1px solid #eee", display: "flex", gap: "0.6rem", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <span style={{ fontSize: "0.78rem", color: "#aaa" }}>
              {checkedCount} suggestion{checkedCount !== 1 ? "s" : ""} selected
            </span>
            <div style={{ display: "flex", gap: "0.55rem" }}>
              <button onClick={onClose} style={{ padding: "0.45rem 0.95rem", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 8, fontWeight: 600, fontSize: "0.83rem", cursor: "pointer", fontFamily: FONT, color: "#555" }}>
                Dismiss
              </button>
              {checkedCount > 0 && (
                <button onClick={applySelected} style={{ padding: "0.45rem 1.1rem", background: NAVY, border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.83rem", cursor: "pointer", fontFamily: FONT, color: "#fff" }}>
                  Apply {checkedCount > 1 ? `${checkedCount} Changes` : "Change"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ onAddTask }) {
  return (
    <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#aaa", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.6rem" }}>
      <span style={{ fontSize: "2rem" }}>✅</span>
      <p style={{ margin: 0, fontSize: "0.9rem", fontStyle: "italic", fontFamily: FONT }}>No tasks match this filter.</p>
      <button onClick={onAddTask} style={{ background: "#E4E69B", border: "none", color: "#333", borderRadius: 7, padding: "0.35rem 0.9rem", fontSize: "0.83rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>+ Add a task</button>
    </div>
  );
}

// ── Generic task board (Today + Tomorrow) ─────────────────────────────────────
function TaskBoard({ tasks, date, heading, isTomorrow, onViewTask, onMoveTask, onAddTask, currentUser, taskTypes }) {
  isOverdue.tz = currentUser?.timezone; // Block D20 Part A: stored timezone for the overdue helper
  const isMobile = typeof window !== "undefined"
    && (window.innerWidth <= 1024 || window.matchMedia('(pointer: coarse)').matches);
  const isMobileViewport = typeof window !== "undefined" && window.matchMedia('(max-width: 767px)').matches;
  const userId = currentUser?.id;
  const isPro = userMeetsPlan(currentUser, 'pro'); // FIX-3: shared, case-insensitive, trial-aware
  const viewKey = `taskView_${isTomorrow ? "tomorrow" : "today"}_${userId || "default"}`;

  const [view, setView] = useState(() => {
    const isPhone = typeof window !== "undefined" && window.matchMedia('(max-width: 767px)').matches;
    const stored = localStorage.getItem(viewKey);
    if (isPhone && stored === "kanban") return "list";
    if (stored) return stored;
    return isPhone ? "list" : "kanban";
  });
  const [activeFilter, setActiveFilter] = useState("all");
  const [dragOverCol, setDragOverCol] = useState(null);
  const [activeTimers, setActiveTimers] = useState({});
  const [loggedMinutes, setLoggedMinutes] = useState({});
  const [clientToast, setClientToast] = useState(null);
  const [showAiPlan, setShowAiPlan] = useState(false);
  const timerRefs = useRef({});

  function changeView(v) { setView(v); localStorage.setItem(viewKey, v); }

  // Fetch logged time totals for visible tasks
  const loadLoggedMinutes = useCallback(async (taskList) => {
    if (!isPro || !taskList.length) return;
    const ids = taskList.map(t => String(t.id)).join(',');
    try {
      const res = await api(`/api/time-entries?taskIds=${ids}`);
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      (data.entries || []).forEach(e => {
        const key = String(e.taskId);
        map[key] = (map[key] || 0) + e.duration;
      });
      setLoggedMinutes(map);
    } catch (_) { }
  }, [isPro]);

  const dateStr = date.toDateString();
  const dateTasks = tasks.filter(t => {
    const ref = t.datetime || (t.date && new Date(t.date + "T00:00:00"));
    return ref && new Date(ref).toDateString() === dateStr;
  });

  // Fetch synced external events for this board's date
  const boardDateStart = new Date(date); boardDateStart.setHours(0, 0, 0, 0);
  const boardDateEnd   = new Date(date); boardDateEnd.setHours(23, 59, 59, 999);
  const localDateStr   = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const { events: allSyncedEvents } = useExternalCalendarEvents(boardDateStart.toISOString(), boardDateEnd.toISOString());
  const syncedEventsForDay = filterEventsForDate(allSyncedEvents, localDateStr);

  useEffect(() => { loadLoggedMinutes(dateTasks); }, [dateTasks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveTimeEntry(taskId, task, elapsed) {
    const durationMins = Math.round(elapsed / 60);
    if (durationMins < 1) return;
    const startDt = new Date(Date.now() - elapsed * 1000);
    try {
      await api('/api/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          taskId: String(taskId),
          projectId: task?.projectId ?? null,
          startTime: startDt.toISOString(),
          endTime: new Date().toISOString(),
          duration: durationMins,
          note: null,
        }),
      });
      // Update local logged total
      setLoggedMinutes(prev => ({ ...prev, [String(taskId)]: (prev[String(taskId)] || 0) + durationMins }));
    } catch (_) { }
  }

  function toggleTimer(taskId, task) {
    setActiveTimers(prev => {
      if (prev[taskId]) {
        // Stopping — save the entry
        clearInterval(timerRefs.current[taskId]);
        delete timerRefs.current[taskId];
        const elapsed = prev[taskId].elapsed;
        saveTimeEntry(taskId, task, elapsed);
        const next = { ...prev }; delete next[taskId]; return next;
      }
      const start = Date.now();
      timerRefs.current[taskId] = setInterval(() => {
        setActiveTimers(p => p[taskId] ? { ...p, [taskId]: { ...p[taskId], elapsed: Math.floor((Date.now() - start) / 1000) } } : p);
      }, 1000);
      return { ...prev, [taskId]: { start, elapsed: 0 } };
    });
  }

  useEffect(() => () => Object.values(timerRefs.current).forEach(clearInterval), []);

  const pillCounts = {
    all: dateTasks.length,
    mine: dateTasks.filter(t => t.userId === userId || t.createdBy === userId).length,
    overdue: dateTasks.filter(isOverdue).length,
    high: dateTasks.filter(t => t.priority === "High" || t.priority === "Critical").length,
  };

  const filtered = dateTasks.filter(t => {
    if (activeFilter === "mine") return t.userId === userId || t.createdBy === userId;
    if (activeFilter === "overdue") return isOverdue(t);
    if (activeFilter === "high") return t.priority === "High" || t.priority === "Critical";
    return true;
  });

  const todo = sortByTime(filtered.filter(t => getStatus(t) === "todo"));
  const inProgress = sortByTime(filtered.filter(t => getStatus(t) === "inProgress"));
  const done = filtered.filter(t => getStatus(t) === "done");

  function handleDrop(e, colId) {
    setDragOverCol(null);
    const taskId = e.dataTransfer.getData("taskId");
    if (!taskId) return;
    onMoveTask(taskId, statusToUpdates(colId));
  }

  // Wrap onViewTask to show client toast for untracked project tasks
  function handleViewTask(task) {
    if (isPro && task?.projectId && !activeTimers[task.id]) {
      setClientToast(task);
    }
    onViewTask(task);
  }

  const dateLabel = date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const dayLabel = isTomorrow ? "tomorrow" : "today";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: isMobile ? "auto" : "calc(100vh - 4rem)",
        padding: isMobile ? "0.75rem" : "1rem 1.25rem",
        gap: "0.75rem",
        fontFamily: FONT,
        overflow: "visible",
        boxSizing: "border-box",
      }}
    >

      {/* ── Top bar ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.5rem", flexShrink: 0 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.25rem", color: NAVY, fontFamily: "'Poppins', sans-serif" }}>{heading}</h2>
          <div style={{ fontSize: "0.82rem", color: "#888", marginTop: "0.1rem" }}>{dateLabel} · {filtered.length} task{filtered.length !== 1 ? "s" : ""}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          {isTomorrow && (
            <button
              onClick={() => setShowAiPlan(v => !v)}
              style={{ padding: "0.48rem 1.05rem", background: "#E4E69B", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", fontFamily: FONT, color: "#333", display: "flex", alignItems: "center", gap: "0.4rem" }}
            >
              🤖 Plan my day with Aidey
            </button>
          )}
          <ViewToggle view={view} onChange={changeView} />
          <button onClick={onAddTask} style={{ padding: "0.48rem 1.05rem", background: "#E4E69B", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", fontFamily: FONT }}>
            + Add Task
          </button>
        </div>
      </div>

      {/* ── Filter pills ── */}
      <div style={{ flexShrink: 0 }}>
        <FilterPills active={activeFilter} onChange={setActiveFilter} counts={pillCounts} />
      </div>

      {/* ── AI plan modal ── */}
      {showAiPlan && isTomorrow && (
        <AiPlanPanel
          tasks={dateTasks}
          date={date}
          dateLabel={dateLabel}
          onClose={() => setShowAiPlan(false)}
          onAccept={onMoveTask}
        />
      )}

      {/* ── Client work toast ── */}
      {clientToast && <ClientWorkToast task={clientToast} onDismiss={() => setClientToast(null)} />}

      {/* ── View body ── */}
      <div style={{ flex: 1, overflow: "visible", display: "flex", flexDirection: "column", minHeight: 0 }}>

        {/* KANBAN */}
        {view === "kanban" && (
          <div
            className="kanban-scroll-container"
            style={{
              flex: 1,
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {(isTomorrow ? COLS.filter(c => c.id === "todo") : COLS).map(col => {
              const colTasks = col.id === "todo" ? todo : col.id === "inProgress" ? inProgress : done;
              return (
                <KanbanCol key={col.id} col={col} tasks={colTasks} onView={handleViewTask} onDrop={handleDrop} onAddTask={onAddTask} isDragOver={dragOverCol === col.id} isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} taskTypes={taskTypes} loggedMinutes={loggedMinutes} isMobileViewport={isMobileViewport} />
              );
            })}
          </div>
        )}

        {/* LIST */}
        {view === "list" && (
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            {isTomorrow ? (
              <TomorrowListView
                filtered={filtered}
                syncedEvents={syncedEventsForDay}
                onView={handleViewTask}
                onMove={onMoveTask}
                isPro={isPro}
                activeTimers={activeTimers}
                onTimerToggle={toggleTimer}
                taskTypes={taskTypes}
                onAddTask={onAddTask}
                loggedMinutes={loggedMinutes}
              />
            ) : (
              <TodayListView
                filtered={filtered}
                syncedEvents={syncedEventsForDay}
                onView={handleViewTask}
                onMove={onMoveTask}
                isPro={isPro}
                activeTimers={activeTimers}
                onTimerToggle={toggleTimer}
                taskTypes={taskTypes}
                onAddTask={onAddTask}
                loggedMinutes={loggedMinutes}
              />
            )}
          </div>
        )}

        {/* PRIORITY */}
        {view === "priority" && (
          <div style={{ flex: 1, overflowY: "auto" }}>
            {filtered.length === 0 ? <EmptyState onAddTask={onAddTask} /> : (
              ["Critical", "High", "Medium", "Low"].map(p => {
                const group = filtered.filter(t => (t.priority || "Medium") === p);
                if (group.length === 0 && p !== "Medium") return null;
                return (
                  <PrioritySection key={p} priority={p} tasks={sortByTime(group)} onView={handleViewTask} onMove={onMoveTask} isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} taskTypes={taskTypes} onAddTask={onAddTask} dayLabel={dayLabel} loggedMinutes={loggedMinutes} />
                );
              })
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Main TaskList export ──────────────────────────────────────────────────────
function TaskList({ tasks, mode, sortBy, setSortBy, onViewTask, today, tomorrow, taskTypes, undoComplete, onMoveTask, onAddTask, currentUser }) {

  const tasksForDate = date => tasks.filter(t => new Date(t.datetime).toDateString() === date.toDateString());
  const sortTasks = list => {
    if (sortBy === "time") return [...list].sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    return [...list].sort((a, b) => {
      const pa = PRIORITY_RANK[a.priority] ?? 2, pb = PRIORITY_RANK[b.priority] ?? 2;
      if (pa !== pb) return pa - pb;
      return new Date(a.datetime) - new Date(b.datetime);
    });
  };

  if (mode === "Today") {
    return <TaskBoard tasks={tasks} date={today} heading="Today's Tasks" isTomorrow={false} onViewTask={onViewTask} onMoveTask={onMoveTask || (() => { })} onAddTask={onAddTask || (() => { })} currentUser={currentUser} taskTypes={taskTypes} />;
  }

  if (mode === "Tomorrow") {
    return <TaskBoard tasks={tasks} date={tomorrow} heading="Tomorrow's Tasks" isTomorrow={true} onViewTask={onViewTask} onMoveTask={onMoveTask || (() => { })} onAddTask={onAddTask || (() => { })} currentUser={currentUser} taskTypes={taskTypes} />;
  }

  // ── WEEK VIEW ──
  if (mode === "Week View") {
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay() + 1);
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
    return (
      <div className="week-view" style={{ display: "flex", gap: "1.5rem" }}>
        {days.map((date, idx) => {
          const dayTasks = sortTasks(tasksForDate(date));
          return (
            <div key={idx} className="task-column" style={{ flex: 1, background: "#f9f9f9", borderRadius: "8px", padding: "1rem", minWidth: "180px" }}>
              <h4 style={{ textAlign: "center", marginBottom: "0.7rem" }}>{date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</h4>
              {dayTasks.length === 0 ? <p style={{ color: "#bbb", textAlign: "center" }}>No tasks</p> :
                dayTasks.map(task => (
                  <div key={task.id} className={`task-card ${(task.priority || "medium").toLowerCase()}`} onClick={() => onViewTask(task)} style={{ marginBottom: "0.7rem", cursor: "pointer" }}>
                    <div style={{ fontWeight: "600", fontSize: "1rem" }}>{task.title}</div>
                    {task.taskType && <div style={{ fontSize: "0.85rem", color: "#666" }}>{taskTypes?.find(t => t.value === task.taskType)?.label}</div>}
                    <div style={{ fontSize: "0.85rem", color: "#888" }}>{fmtTime(task.datetime)} – {fmtTime(task.deadline) || "No deadline"}</div>
                  </div>
                ))
              }
            </div>
          );
        })}
      </div>
    );
  }

  // ── LIST VIEW (All Tasks) ──
  if (mode === "List") {
    return (
      <AllTasksView
        tasks={tasks}
        onViewTask={onViewTask}
        onMoveTask={onMoveTask || (() => { })}
        onAddTask={onAddTask || (() => { })}
        currentUser={currentUser}
        taskTypes={taskTypes}
      />
    );
  }

  return null;
}

export default TaskList;
